import React from "react";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaBriefcase, FaExclamationCircle, FaFileDownload, FaUser } from "react-icons/fa";
import PageHeader from "src/components/pageHeader";
import { useCvFile } from "../hooks/useCvFile";
import { JobCandidate, RecruiterCv, RecruiterRecordDetail } from "../types/recruiter.entity";
import {
  bsnLabel,
  cvIsImage,
  cvTypeLabel,
  educationLabel,
  formatDateTime,
  formatFileSize,
  residencyLabel,
  startWhenLabel,
  transportLabel,
  travelDistanceLabel,
  yesNoLabel,
} from "../utils/recruiter.labels";

type Props = {
  record: RecruiterRecordDetail;
  onBack: () => void;
};

const KIND_TITLE: Record<RecruiterRecordDetail["kind"], string> = {
  applications: "Application",
  "help-requests": "Help request",
  searches: "Saved search",
};

/** The same three, lower-cased for mid-sentence use in the CV wording. */
const KIND_NOUN: Record<RecruiterRecordDetail["kind"], string> = {
  applications: "application",
  "help-requests": "help request",
  searches: "saved search",
};

const section = "border border-slate-200 bg-white p-5 shadow-sm dark:border-gray-700 dark:bg-gray-900";
const heading = "mb-4 flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-gray-100";
const label = "mb-1 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-gray-400";
const value = "text-sm text-slate-800 dark:text-gray-200";
const muted = "text-sm text-slate-500 dark:text-gray-400";

const Field = ({ name, children }: { name: string; children: React.ReactNode }) => (
  <div>
    <p className={label}>{name}</p>
    <div className={value}>{children ?? <span className={muted}>—</span>}</div>
  </div>
);

/**
 * The learner's CV, opened straight from Storage.
 *
 * `job_cvs` is readable by the owner and by ADMIN (storage.rules, applied in
 * the console — that file is not deployed from the backend repo). If that rule
 * is not live yet the URL never resolves and the page says so instead of
 * showing a dead button.
 *
 * Two things are said before it is opened, because both were guessed at before.
 *
 * **Whose record it belongs to.** The path arrives already resolved by
 * `resolveCv`, which scopes it to this record's conversation. A `PROFILE`
 * source means the CV is the learner's current one with nothing tying it to
 * this record, and that is stated rather than implied — it is how a screenshot
 * uploaded weeks earlier came to sit under an application that carried no CV.
 *
 * **What the file is.** A learner may send a photo or a screenshot instead of a
 * document, which the recruiter accepts on purpose. A reader who clicks a
 * button labelled only "Open CV" has no way to tell until a new tab has
 * already opened. The type, size and upload time come from the object itself,
 * so they describe what will actually render.
 */
const CvLink = ({ cv, kindNoun }: { cv: RecruiterCv; kindNoun: string }) => {
  const { file, loading } = useCvFile(cv.storagePath);

  if (loading) return <p className={muted}>Loading CV…</p>;

  if (!file) {
    return (
      <p className="text-sm text-red-700 dark:text-red-300">
        Could not open the CV. The file may have been deleted, or the Storage rule that lets admins read CVs is not
        applied on this environment yet.
      </p>
    );
  }

  const isThisRecord = cv.source === "THIS_RECORD";

  return (
    <div className="space-y-2">
      <p className={label}>{isThisRecord ? `CV sent with this ${kindNoun}` : "CV on the learner's profile"}</p>

      {!isThisRecord && (
        <p className="text-sm text-amber-700 dark:text-amber-300">
          {cv.source === "PROFILE"
            ? `This file is the learner's current CV. It is not recorded against this ${kindNoun}, so they may have sent no CV with it.`
            : `Whether this file was sent with this ${kindNoun} could not be checked: the CV upload ledger is not readable on this environment yet.`}
        </p>
      )}

      <a
        href={file.url}
        target="_blank"
        rel="noreferrer"
        className="inline-flex items-center gap-2 bg-emerald-600 px-4 py-2 text-sm font-medium text-white no-underline hover:bg-emerald-700"
      >
        <FaFileDownload />
        Open CV
      </a>
      <p className={muted}>
        {cvTypeLabel(file.contentType)} · {formatFileSize(file.bytes)}
        {file.uploadedAt && ` · uploaded ${formatDateTime(file.uploadedAt)}`}
      </p>
      {cvIsImage(file.contentType) && (
        <p className="text-xs text-amber-700 dark:text-amber-300">
          This is a photo or a screenshot, not a document. If it is not readable, ask the learner for a PDF.
        </p>
      )}
    </div>
  );
};

const CandidateProfile = ({
  candidate,
  cv,
  kindNoun,
}: {
  candidate: JobCandidate;
  cv: RecruiterCv | null;
  kindNoun: string;
}) => {
  return (
    <div className="space-y-5">
      {candidate.profileCorrection && (
        <div className="flex gap-3 border border-amber-300 bg-amber-50 p-4 text-amber-900 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-200">
          <FaExclamationCircle className="mt-0.5 shrink-0" />
          <div>
            <p className="text-sm font-semibold">The learner says their profile is wrong</p>
            <p className="mt-1 whitespace-pre-wrap text-sm">{candidate.profileCorrection}</p>
            <p className="mt-2 text-xs">
              The recruiter cannot change a learner's municipality or Dutch level. Someone has to, on their user page.
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Field name="Residency">{residencyLabel(candidate.residencyStatus, candidate.residencyDeclined)}</Field>
        <Field name="Has a BSN">{bsnLabel(candidate.hasBsn)}</Field>
        <Field name="Travels by">{transportLabel(candidate.transport)}</Field>
        <Field name="Highest education">{educationLabel(candidate.highestEducation)}</Field>
        <Field name="Has worked before">{yesNoLabel(candidate.hasWorkExperience)}</Field>
      </div>

      {candidate.experienceText && (
        <Field name="Work experience, in their words">
          <p className="whitespace-pre-wrap">{candidate.experienceText}</p>
        </Field>
      )}

      {!!candidate.skills?.length && (
        <Field name="Skills they confirmed">
          <div className="flex flex-wrap gap-2">
            {candidate.skills.map((skill) => (
              <span
                key={skill}
                className="border border-violet-200 bg-violet-50 px-2 py-1 text-xs font-medium text-violet-700 dark:border-violet-700 dark:bg-violet-950/40 dark:text-violet-300"
              >
                {skill}
              </span>
            ))}
          </div>
        </Field>
      )}

      <div className="border-t border-slate-100 pt-4 dark:border-gray-700">
        {cv ? (
          <CvLink cv={cv} kindNoun={kindNoun} />
        ) : candidate.cvStoragePath ? (
          // A path that exists but names someone else's folder. An admin may
          // read every CV; this page opens only the one this learner uploaded.
          <p className="text-sm text-red-700 dark:text-red-300">
            The CV on file is not stored in this learner's folder, so it is not opened here.
          </p>
        ) : (
          <p className={muted}>No CV on file for this learner.</p>
        )}
      </div>
    </div>
  );
};

export const RecruiterRecordDetails = ({ record, onBack }: Props) => {
  const isSearch = record.kind === "searches";

  return (
    <div className="page_container">
      <PageHeader text={KIND_TITLE[record.kind]} />

      <button
        onClick={onBack}
        className="mb-5 inline-flex items-center gap-2 border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-100"
      >
        <FaArrowLeft />
        Back to the inbox
      </button>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <section className={section}>
          <div className={heading}>
            <FaUser className="text-emerald-600" /> Learner
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field name="Name">
              {record.learnerName ? (
                <Link to={`/users/${record.userId}`} className="text-emerald-700 no-underline hover:underline">
                  {record.learnerName}
                </Link>
              ) : (
                <span className={muted}>User not found</span>
              )}
            </Field>
            <Field name="Email">{record.learnerEmail}</Field>
            <Field name="Received">{formatDateTime(record.createdAt)}</Field>
            <Field name="Deleted on">{formatDateTime(record.expiresAt)}</Field>
          </div>
        </section>

        {!isSearch && (
          <section className={section}>
            <div className={heading}>
              <FaBriefcase className="text-sky-600" /> Vacancy
            </div>
            {record.job ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <Field name="Title">{record.job.title}</Field>
                <Field name="Employer">{record.job.employer}</Field>
                <Field name="Municipality">{record.job.gemeente}</Field>
                <Field name="Code">{record.job.jobCode}</Field>
                <Field name="Vacancy page">
                  <a href={record.job.sourceUrl} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline">
                    Open on the employer's site
                  </a>
                  {record.job.status !== "ACTIVE" && (
                    <p className="mt-1 text-xs text-amber-600">No longer in the catalogue; the page may be gone.</p>
                  )}
                </Field>
              </div>
            ) : (
              <p className={muted}>
                No longer listed. Vacancy code <span className="font-mono">{record.jobCode ?? record.jobId}</span>.
              </p>
            )}
          </section>
        )}

        <section className={`${section} xl:col-span-2`}>
          <div className={heading}>What they told the recruiter</div>

          {record.kind === "applications" && (
            <Field name="Why they want this job">
              {record.motivation ? (
                <p className="whitespace-pre-wrap">{record.motivation}</p>
              ) : (
                <span className={muted}>No motivation given.</span>
              )}
            </Field>
          )}

          {record.kind === "help-requests" && (
            <div className="space-y-4">
              <Field name="Still unanswered — these need a person">
                {record.unanswered?.length ? (
                  <ul className="list-disc space-y-1 pl-5">
                    {record.unanswered.map((q, i) => (
                      <li key={i} className="font-medium">
                        {q}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className={muted}>None. The recruiter answered everything from the vacancy text.</span>
                )}
              </Field>
              <Field name="Everything they asked">
                {record.questions?.length ? (
                  <ol className="list-decimal space-y-1 pl-5">
                    {record.questions.map((q, i) => (
                      <li key={i}>{q}</li>
                    ))}
                  </ol>
                ) : (
                  <span className={muted}>They looked at the job without asking anything.</span>
                )}
              </Field>
            </div>
          )}

          {isSearch && (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
              <Field name="Looking for">{record.desiredWork}</Field>
              <Field name="Can start">{startWhenLabel(record.startWhen)}</Field>
              <Field name="Will travel">{travelDistanceLabel(record.travelDistance)}</Field>
            </div>
          )}
        </section>

        <section className={`${section} xl:col-span-2`}>
          <div className={heading}>Candidate profile</div>
          {record.candidate ? (
            <>
              {record.kind === "help-requests" && (
                <p className={`${muted} mb-4`}>
                  From another conversation. Asking for help does not collect a profile.
                </p>
              )}
              <CandidateProfile
                candidate={record.candidate}
                cv={record.cv}
                kindNoun={KIND_NOUN[record.kind]}
              />
            </>
          ) : (
            <p className={muted}>
              {record.kind === "help-requests"
                ? "None. Asking for help does not collect a profile."
                : "Profile no longer on file. It may have been deleted with the learner's account."}
            </p>
          )}
        </section>
      </div>
    </div>
  );
};

export default RecruiterRecordDetails;
